import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import { Button } from "./ui/button";
import { API, useUser } from "../contexts/UserContext";

const Quiz = ({ topic, onComplete }) => {
 const { user, refreshProgress } = useUser();
 const quizzes = topic.quizzes || [];
 const [answers, setAnswers] = useState(Array(quizzes.length).fill(null));
 const [review, setReview] = useState(null);
 const [submitting, setSubmitting] = useState(false);

 const setAnswer = (qIdx, optIdx) => {
 if (review) return;
 const next = [...answers];
 next[qIdx] = optIdx;
 setAnswers(next);
 };

 const submit = async () => {
 if (answers.some((a) => a === null)) return;
 setSubmitting(true);
 try {
 const res = await axios.post(`${API}/progress/quiz`, {
 user_id: user.id,
 topic_id: topic.id,
 answers,
 });
 setReview(res.data);
 await refreshProgress();
 onComplete && onComplete(res.data);
 } catch (e) {
 console.error(e);
 } finally {
 setSubmitting(false);
 }
 };

 const reset = () => {
 setAnswers(Array(quizzes.length).fill(null));
 setReview(null);
 };

 if (quizzes.length === 0) return null;

 return (
 <section
 data-testid={`quiz-section-${topic.id}`}
 className="mt-12 border-t-2 border-orange-500 pt-8"
 >
 <div className="flex items-center gap-3 mb-6">
 <div className="w-8 h-8 rounded-full bg-orange-500 text-black font-bold flex items-center justify-center">Q</div>
 <h3 className="font-heading text-2xl font-bold text-zinc-900 ">
 Quick Check — {quizzes.length} question{quizzes.length > 1 ? "s" : ""}
 </h3>
 </div>

 {quizzes.map((q, qi) => {
 const userAnswer = review ? review.review[qi].user_answer : answers[qi];
 const correct = review ? review.review[qi].correct : null;
 return (
 <div
 key={qi}
 data-testid={`quiz-question-${topic.id}-${qi}`}
 className="mb-8 p-5 border border-zinc-200 rounded-sm bg-white "
 >
 <div className="font-semibold text-base md:text-lg mb-4 text-zinc-900 ">
 {qi + 1}. {q.q}
 </div>
 <div className="space-y-2">
 {q.options.map((opt, oi) => {
 const selected = userAnswer === oi;
 const isCorrect = review && oi === correct;
 const isWrongSelected = review && selected && oi !== correct;
 let cls =
 "border border-zinc-200 hover:border-orange-500 :border-orange-500 cursor-pointer";
 if (review) {
 if (isCorrect) cls = "border-2 border-green-500 bg-green-50 ";
 else if (isWrongSelected) cls = "border-2 border-red-500 bg-red-50 ";
 else cls = "border border-zinc-200 opacity-70";
 } else if (selected) {
 cls = "border-2 border-orange-500 bg-orange-50 ";
 }
 return (
 <label
 key={oi}
 data-testid={`quiz-option-${topic.id}-${qi}-${oi}`}
 className={`flex items-center gap-3 p-3 rounded-sm transition-all ${cls}`}
 >
 <input
 type="radio"
 name={`q-${topic.id}-${qi}`}
 checked={selected || false}
 onChange={() => setAnswer(qi, oi)}
 disabled={!!review}
 className="accent-orange-500"
 />
 <span className="flex-1 text-sm md:text-base text-zinc-800 ">{opt}</span>
 {review && isCorrect && <CheckCircle2 className="w-5 h-5 text-green-600" />}
 {review && isWrongSelected && <XCircle className="w-5 h-5 text-red-600" />}
 </label>
 );
 })}
 </div>
 {review && (
 <div
 data-testid={`quiz-explanation-${topic.id}-${qi}`}
 className="mt-4 p-3 bg-zinc-50 border-l-4 border-orange-500 rounded-sm text-sm md:text-base text-zinc-700 "
 >
 <span className="font-semibold text-zinc-900 ">Why:</span>{" "}
 {review.review[qi].explain}
 </div>
 )}
 </div>
 );
 })}

 {!review ? (
 <Button
 data-testid={`submit-quiz-btn-${topic.id}`}
 onClick={submit}
 disabled={answers.some((a) => a === null) || submitting}
 className="bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-sm px-6 py-5 text-base"
 >
 {submitting ? "Checking…" : "Submit answers"}
 </Button>
 ) : (
 <div className="flex items-center gap-4 flex-wrap">
 <div
 data-testid={`quiz-score-${topic.id}`}
 className="px-5 py-3 bg-orange-500 text-black font-bold rounded-sm"
 >
 Score: {review.score} / {review.total}
 </div>
 <Button
 variant="outline"
 onClick={reset}
 data-testid={`quiz-retry-btn-${topic.id}`}
 className="rounded-sm"
 >
 <RotateCcw className="w-4 h-4 mr-2" />
 Try again
 </Button>
 </div>
 )}
 </section>
 );
};

export default Quiz;
